import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import TeamIconPicker, { getTeamIconById, getRandomIcon, TeamIconPreview, type TeamIcon } from "../components/TeamIconPicker";
import LeaguePlayersTab from "../components/league/LeaguePlayersTab";
import LeagueStatsTab from "../components/league/LeagueStatsTab";
import {
  listLeagues,
  getLeagueStandings,
  getLeagueFixtures,
  createLeague,
  deleteLeague,
  getLeagueTeams,
  addTeamToLeague,
  updateLeagueTeam,
  removeTeamFromLeague,
  generateLeagueFixtures,
  deleteAllLeagueFixtures,
  getLeagueTableEntries,
  initializeLeagueTable,
  League,
  LeagueStanding,
  LeagueFixture,
  LeagueTeam,
  LeagueTableEntry,
} from "../store/leagues";
import { supabase } from "../supabaseClient";
import { generateFixturesByType, CompetitionType } from "../utils/fixtureGenerator";

interface LeaguesProps {
  onClose: () => void;
  onStartMatchFromFixture?: (fixture: LeagueFixture, league: League) => void;
  onViewMatch?: (matchId: string) => void;
  autoCreate?: boolean;
}

type TabView = "overview" | "table" | "fixtures" | "results" | "teams" | "players" | "stats";

export default function Leagues({ onClose, onStartMatchFromFixture, onViewMatch, autoCreate }: LeaguesProps) {
  const { user } = useAuth();
  const [leagues, setLeagues] = useState<League[]>([]);
  const [selectedLeague, setSelectedLeague] = useState<League | null>(null);
  const [currentTab, setCurrentTab] = useState<TabView>("overview");
  const [standings, setStandings] = useState<LeagueStanding[]>([]);
  const [tableEntries, setTableEntries] = useState<LeagueTableEntry[]>([]);
  const [fixtures, setFixtures] = useState<LeagueFixture[]>([]);
  const [leagueTeams, setLeagueTeams] = useState<LeagueTeam[]>([]);
  const [teamNames, setTeamNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [showCreateLeague, setShowCreateLeague] = useState(false);
  const [showCreateFixture, setShowCreateFixture] = useState(false);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [editingTeam, setEditingTeam] = useState<LeagueTeam | null>(null);
  const [showGenerateFixtures, setShowGenerateFixtures] = useState(false);

  // Auto-open create-league modal when requested
  useEffect(() => {
    if (autoCreate && !loading && !selectedLeague) {
      setShowCreateLeague(true);
    }
  }, [autoCreate, loading, selectedLeague]);

  useEffect(() => {
    loadLeagues();
  }, []);

  useEffect(() => {
    if (selectedLeague) {
      loadLeagueData();
    }
  }, [selectedLeague, currentTab]);

  async function loadLeagues() {
    setLoading(true);
    const data = await listLeagues();
    setLeagues(data);
    setLoading(false);
  }

  async function loadLeagueData() {
    if (!selectedLeague) return;

    if (currentTab === "overview") {
      const [entries, fixturesData, teams, standingsData] = await Promise.all([
        getLeagueTableEntries(selectedLeague.id),
        getLeagueFixtures(selectedLeague.id),
        getLeagueTeams(selectedLeague.id),
        getLeagueStandings(selectedLeague.id),
      ]);
      if (entries.length === 0) {
        await initializeLeagueTable(selectedLeague.id);
        const freshEntries = await getLeagueTableEntries(selectedLeague.id);
        setTableEntries(freshEntries);
      } else {
        setTableEntries(entries);
      }
      setStandings(standingsData);
      const names = new Map<string, string>();
      teams.forEach((t) => names.set(t.id, t.team_name));
      setTeamNames(names);
      setFixtures(fixturesData.map((f) => ({
        ...f,
        home_team_name: names.get(f.home_team_id) || "Unknown Team",
        away_team_name: names.get(f.away_team_id) || "Unknown Team",
      })));
      setLeagueTeams(teams);
    } else if (currentTab === "table") {
      const [entries, standingsData] = await Promise.all([
        getLeagueTableEntries(selectedLeague.id),
        getLeagueStandings(selectedLeague.id),
      ]);
      if (entries.length === 0) {
        await initializeLeagueTable(selectedLeague.id);
        const freshEntries = await getLeagueTableEntries(selectedLeague.id);
        setTableEntries(freshEntries);
      } else {
        setTableEntries(entries);
      }
      setStandings(standingsData);
    } else if (currentTab === "fixtures" || currentTab === "results") {
      const fixturesData = await getLeagueFixtures(selectedLeague.id);
      const names = await loadTeamNamesMap();
      setTeamNames(names);
      setFixtures(fixturesData.map((f) => ({
        ...f,
        home_team_name: names.get(f.home_team_id) || "Unknown Team",
        away_team_name: names.get(f.away_team_id) || "Unknown Team",
      })));
    } else if (currentTab === "teams") {
      const teams = await getLeagueTeams(selectedLeague.id);
      const names = await loadTeamNamesMap();
      setTeamNames(names);
      setLeagueTeams(teams);
    }
  }

  async function loadTeamNamesMap(): Promise<Map<string, string>> {
    if (!selectedLeague) return new Map();

    const teams = await getLeagueTeams(selectedLeague.id);
    const nameMap = new Map<string, string>();

    for (const team of teams) {
      nameMap.set(team.id, team.team_name);
    }

    return nameMap;
  }

  async function handleCreateLeague(leagueData: Omit<League, "id" | "user_id" | "created_at">) {
    console.log('[Leagues] handleCreateLeague called with:', leagueData);
    const created = await createLeague(leagueData);
    if (created) {
      console.log('[Leagues] League created:', created.id);
      await loadLeagues();
      setShowCreateLeague(false);
      setSelectedLeague(created);
    } else {
      console.error('[Leagues] createLeague returned null');
      alert('Could not create league. Please check your connection and try again.');
    }
  }

  async function handleDeleteLeague() {
    if (!selectedLeague) return;
    const confirmed = confirm(`Delete league "${selectedLeague.name}"? This will remove all fixtures and standings.`);
    if (confirmed) {
      await deleteLeague(selectedLeague.id);
      await loadLeagues();
      setSelectedLeague(null);
    }
  }

  function handleStartMatch(fixture: LeagueFixture) {
    if (!selectedLeague) return;

    // If match already exists, open it instead of creating new one
    if (fixture.match_id) {
      if (onViewMatch) {
        onViewMatch(fixture.match_id);
        onClose();
      }
      return;
    }

    // Create new match from fixture
    if (onStartMatchFromFixture) {
      onStartMatchFromFixture(fixture, selectedLeague);
      onClose();
    }
  }

  async function handleViewResult(fixture: LeagueFixture) {
    if (fixture.match_id && onViewMatch) {
      onViewMatch(fixture.match_id);
      onClose();
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 lg:left-64 lg:z-30 bg-gradient-to-br from-[#012b1b] via-[#064428] to-[#012b1b] z-50 flex items-center justify-center">
        <div className="text-white text-xl animate-pulse">Loading leagues...</div>
      </div>
    );
  }

  const isActive = selectedLeague && (!selectedLeague.end_date || new Date(selectedLeague.end_date) >= new Date());
  const nextFixture = fixtures.find((f) => f.status !== "COMPLETED" && f.status !== "played");
  const topTableEntries = tableEntries.slice(0, 4);

  const tabItems: { key: TabView; label: string; icon: string }[] = [
    { key: "overview", label: "Overview", icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" },
    { key: "fixtures", label: "Fixtures", icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" },
    { key: "results", label: "Results", icon: "M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138c.11.73.39 1.398.806 1.946a3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" },
    { key: "teams", label: "Teams", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" },
    { key: "table", label: "Table", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
    { key: "players", label: "Players", icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" },
    { key: "stats", label: "Stats", icon: "M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" },
  ];

  return (
    <div className="fixed inset-0 lg:left-64 lg:z-30 bg-gradient-to-br from-[#012b1b] via-[#064428] to-[#012b1b] z-50 overflow-y-auto">
      {/* Header */}
      <div className="bg-[#0a1f16]/90 backdrop-blur-md border-b border-[#0b5c33]/50 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-3 flex items-center justify-between">
          <button
            onClick={onClose}
            className="text-green-300 hover:text-white p-2 hover:bg-[#0b5c33]/50 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-bold text-white">Leagues</h1>
          <button
            onClick={() => setShowCreateLeague(true)}
            className="text-[#0f9d3d] hover:text-green-300 p-2 hover:bg-[#0b5c33]/50 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 lg:px-8 pt-4 pb-24">
        {leagues.length === 0 ? (
          <div className="rounded-2xl p-10 text-center border border-[#0b5c33]/50 bg-[#064428]/30 mt-4">
            <div className="w-16 h-16 rounded-full bg-[#0f9d3d]/10 border border-[#0f9d3d]/30 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-[#0f9d3d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138c.11.73.39 1.398.806 1.946a3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-2 text-white">No Leagues Yet</h3>
            <p className="text-green-300/80 text-sm mb-6">Create your first league to organize matches and track standings</p>
            <button
              onClick={() => setShowCreateLeague(true)}
              className="bg-[#0f9d3d] hover:bg-green-600 px-6 py-3 rounded-xl font-semibold text-white transition-colors"
            >
              Create Your First League
            </button>
          </div>
        ) : selectedLeague ? (
          <>
            {/* Back to all leagues */}
            <button
              onClick={() => setSelectedLeague(null)}
              className="mb-4 inline-flex items-center gap-2 text-green-300 hover:text-white text-sm font-semibold px-3 py-2 rounded-lg hover:bg-[#0b5c33]/40 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              All Leagues
            </button>

            {/* League Hero Card */}
            <div className="league-hero-card relative rounded-2xl overflow-hidden border border-[#0b5c33]/50 mb-5">
              <div className="league-hero-bg" />
              <div className="relative z-10 p-5">
                <div className="flex items-start gap-4">
                  {/* League Badge */}
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-gradient-to-br from-[#0f9d3d] to-[#064428] border-2 border-[#0f9d3d]/50 flex items-center justify-center flex-shrink-0 shadow-lg">
                    <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138c.11.73.39 1.398.806 1.946a3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </div>

                  {/* League Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h2 className="text-xl sm:text-2xl font-black text-white leading-tight">{selectedLeague.name}</h2>
                      {selectedLeague.start_date && (
                        <span className="text-[10px] font-bold bg-[#0f9d3d] text-white px-2 py-0.5 rounded-md flex-shrink-0">
                          {new Date(selectedLeague.start_date).getFullYear()}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-1.5 text-green-300 text-sm">
                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138c.11.73.39 1.398.806 1.946a3.42 3.42 0 010 4.438" />
                        </svg>
                        <span>{selectedLeague.format} League</span>
                      </div>
                      {selectedLeague.start_date && (
                        <div className="flex items-center gap-1.5 text-green-300/80 text-sm">
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span>
                            {new Date(selectedLeague.start_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                            {selectedLeague.end_date && ` - ${new Date(selectedLeague.end_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`}
                          </span>
                        </div>
                      )}
                      {selectedLeague.location && (
                        <div className="flex items-center gap-1.5 text-green-300/80 text-sm">
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                          <span>{selectedLeague.location}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status + Actions */}
                <div className="flex items-center justify-between mt-4">
                  <span className={`text-xs font-bold px-3 py-1 rounded-md ${
                    isActive
                      ? "bg-[#0f9d3d]/20 text-[#0f9d3d] border border-[#0f9d3d]/30"
                      : "bg-slate-600/20 text-slate-400 border border-slate-500/30"
                  }`}>
                    {isActive ? "IN PROGRESS" : "COMPLETED"}
                  </span>
                  <button
                    onClick={handleDeleteLeague}
                    className="text-red-400/60 hover:text-red-400 text-xs px-3 py-1.5 hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-1 overflow-x-auto lg:flex-wrap lg:overflow-visible pb-2 mb-5 scrollbar-hide border-b border-[#0b5c33]/40">
              {tabItems.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setCurrentTab(tab.key)}
                  className={`flex flex-col items-center gap-1 px-4 py-2.5 rounded-xl transition-all whitespace-nowrap min-w-[64px] ${
                    currentTab === tab.key
                      ? "bg-[#0b5c33]/60 text-white"
                      : "text-green-300/60 hover:text-green-200 hover:bg-[#0b5c33]/20"
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={tab.icon} />
                  </svg>
                  <span className="text-[11px] font-semibold">{tab.label}</span>
                  {currentTab === tab.key && <div className="w-4 h-0.5 bg-[#0f9d3d] rounded-full" />}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {currentTab === "overview" && (
              <LeagueOverviewTab
                league={selectedLeague}
                nextFixture={nextFixture || null}
                tableEntries={topTableEntries}
                standings={standings}
                teams={leagueTeams}
                onTabChange={setCurrentTab}
                onStartMatch={handleStartMatch}
              />
            )}

            {currentTab === "table" && (
              <LeagueTableView entries={tableEntries} standings={standings} />
            )}

            {currentTab === "fixtures" && (
              <FixturesView
                fixtures={fixtures.filter((f) => f.status !== "COMPLETED")}
                leagueId={selectedLeague.id}
                onCreateFixture={() => setShowCreateFixture(true)}
                onRefresh={loadLeagueData}
                onStartMatch={handleStartMatch}
                onGenerateFixtures={() => setShowGenerateFixtures(true)}
              />
            )}

            {currentTab === "results" && (
              <ResultsView fixtures={fixtures.filter((f) => f.status === "COMPLETED")} onViewResult={handleViewResult} />
            )}

            {currentTab === "teams" && (
              <TeamsView
                teams={leagueTeams}
                onAddTeam={() => { setEditingTeam(null); setShowAddTeam(true); }}
                onEditTeam={(team) => { setEditingTeam(team); setShowAddTeam(true); }}
                onRemoveTeam={async (teamRecordId) => {
                  if (selectedLeague) {
                    await removeTeamFromLeague(selectedLeague.id, teamRecordId);
                    await loadLeagueData();
                  }
                }}
              />
            )}

            {currentTab === "players" && selectedLeague && (
              <LeaguePlayersTab leagueId={selectedLeague.id} teams={leagueTeams} />
            )}

            {currentTab === "stats" && selectedLeague && (
              <LeagueStatsTab leagueId={selectedLeague.id} teams={leagueTeams} />
            )}
          </>
        ) : (
          <LeagueListView
            leagues={leagues}
            onSelect={(l) => { setSelectedLeague(l); setCurrentTab("overview"); }}
            onCreate={() => setShowCreateLeague(true)}
          />
        )}
      </div>

      {/* Modals */}
      {showCreateLeague && (
        <CreateLeagueModal onClose={() => setShowCreateLeague(false)} onCreate={handleCreateLeague} />
      )}
      {showCreateFixture && selectedLeague && (
        <CreateFixtureModal league={selectedLeague} teams={leagueTeams} teamNames={teamNames} onClose={() => setShowCreateFixture(false)} onCreated={loadLeagueData} />
      )}
      {showAddTeam && selectedLeague && (
        <AddTeamModal leagueId={selectedLeague.id} onClose={() => { setShowAddTeam(false); setEditingTeam(null); }} onAdded={loadLeagueData} editTeam={editingTeam} />
      )}
      {showGenerateFixtures && selectedLeague && (
        <GenerateFixturesModal league={selectedLeague} teams={leagueTeams} existingFixtures={fixtures} onClose={() => setShowGenerateFixtures(false)} onGenerated={loadLeagueData} />
      )}

      <style>{`
        .league-hero-card {
          background: linear-gradient(135deg, rgba(6, 68, 40, 0.6), rgba(1, 43, 27, 0.8));
        }
        .league-hero-bg {
          position: absolute;
          inset: 0;
          background:
            url('https://images.pexels.com/photos/3628912/pexels-photo-3628912.jpeg?auto=compress&cs=tinysrgb&w=600') center top / cover no-repeat;
          opacity: 0.12;
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}

// League List View Component
function LeagueListView({
  leagues,
  onSelect,
  onCreate,
}: {
  leagues: League[];
  onSelect: (league: League) => void;
  onCreate: () => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-black text-white">Your Leagues</h2>
          <p className="text-green-300/70 text-sm mt-0.5">{leagues.length} {leagues.length === 1 ? "league" : "leagues"}</p>
        </div>
        <button
          onClick={onCreate}
          className="inline-flex items-center gap-2 bg-[#0f9d3d] hover:bg-green-600 text-white px-4 py-2.5 rounded-xl font-semibold transition-colors text-sm flex-shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          <span className="hidden sm:inline">Create League</span>
          <span className="sm:hidden">Create</span>
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {leagues.map((league) => {
          const active = !league.end_date || new Date(league.end_date) >= new Date();
          const dateStr = league.start_date || league.created_at;
          const year = dateStr ? new Date(dateStr).getFullYear() : null;
          return (
            <button
              key={league.id}
              onClick={() => onSelect(league)}
              className="group text-left bg-[#064428]/40 hover:bg-[#064428]/70 border border-[#0b5c33]/50 hover:border-[#0f9d3d]/60 rounded-2xl p-5 transition-all"
            >
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0f9d3d] to-[#064428] border border-[#0f9d3d]/40 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138c.11.73.39 1.398.806 1.946a3.42 3.42 0 010 4.438" /></svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-bold text-lg leading-tight truncate group-hover:text-[#0f9d3d] transition-colors">{league.name}</h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-[11px] font-bold text-green-200 bg-[#0b5c33]/50 px-2 py-0.5 rounded">{league.format}</span>
                    {year && <span className="text-[11px] text-green-300/70">Season {year}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4">
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-md ${active ? "bg-[#0f9d3d]/20 text-[#0f9d3d] border border-[#0f9d3d]/30" : "bg-slate-600/20 text-slate-300 border border-slate-500/30"}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-[#0f9d3d]" : "bg-slate-400"}`} />
                  {active ? "In Progress" : "Completed"}
                </span>
                <svg className="w-5 h-5 text-green-300/40 group-hover:text-[#0f9d3d] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// League Overview Tab Component
function LeagueOverviewTab({
  league,
  nextFixture,
  tableEntries,
  standings,
  teams,
  onTabChange,
  onStartMatch,
}: {
  league: League;
  nextFixture: LeagueFixture | null;
  tableEntries: LeagueTableEntry[];
  standings?: LeagueStanding[];
  teams: LeagueTeam[];
  onTabChange: (tab: TabView) => void;
  onStartMatch: (fixture: LeagueFixture) => void;
}) {
  const getTeamIcon = (teamId: string | undefined | null) => {
    if (!teamId) return null;
    const team = teams.find((t) => t.id === teamId);
    if (!team) return null;
    return team.team_icon_id ? getTeamIconById(team.team_icon_id) : null;
  };

  const getTeamLogo = (teamId: string | undefined | null) => {
    if (!teamId) return null;
    const team = teams.find((t) => t.id === teamId);
    return team?.team_logo_url || null;
  };

  const getTeamName = (teamId: string | undefined | null) => {
    if (!teamId) return "TBD";
    const team = teams.find((t) => t.id === teamId);
    return team?.team_name || "TBD";
  };

  const homeTeamId = nextFixture?.home_team_id || nextFixture?.team_a_id || "";
  const awayTeamId = nextFixture?.away_team_id || nextFixture?.team_b_id || "";

  return (
    <div className="space-y-5 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-5 lg:items-start">
      {/* Next Match Card */}
      {nextFixture ? (
        <div className="bg-[#064428]/50 rounded-xl border border-[#0b5c33]/50 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-[#0b5c33]/40 flex items-center justify-between">
            <span className="text-xs font-bold text-[#0f9d3d] uppercase tracking-wider">Next Match</span>
            {nextFixture.round_number && (
              <span className="text-[10px] font-semibold text-green-300/60 bg-[#0b5c33]/30 px-2 py-0.5 rounded">
                Round {nextFixture.round_number}
              </span>
            )}
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between gap-3">
              {/* Home Team */}
              <div className="flex-1 flex flex-col items-center text-center gap-2">
                {(() => {
                  const logo = getTeamLogo(homeTeamId);
                  if (logo) {
                    return <img src={logo} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-[#0f9d3d]/40" />;
                  }
                  const icon = getTeamIcon(homeTeamId);
                  if (icon) {
                    return (
                      <div className="w-12 h-12 rounded-full flex items-center justify-center border border-white/10" style={{ backgroundColor: icon.bgColor }}>
                        <svg className="w-6 h-6" viewBox="0 0 24 24" style={{ color: icon.iconColor }} dangerouslySetInnerHTML={{ __html: icon.svg }} />
                      </div>
                    );
                  }
                  const name = getTeamName(homeTeamId);
                  return (
                    <div className="w-12 h-12 rounded-full bg-[#0f9d3d]/20 flex items-center justify-center border-2 border-[#0f9d3d]/40">
                      <span className="text-[#0f9d3d] font-bold text-lg">{name.charAt(0)}</span>
                    </div>
                  );
                })()}
                <span className="text-white text-sm font-semibold leading-tight">
                  {nextFixture.team_a_name || nextFixture.home_team_name || getTeamName(homeTeamId)}
                </span>
              </div>

              {/* VS */}
              <div className="flex flex-col items-center gap-1 px-2">
                <span className="text-green-300/50 text-xs font-bold">VS</span>
              </div>

              {/* Away Team */}
              <div className="flex-1 flex flex-col items-center text-center gap-2">
                {(() => {
                  const logo = getTeamLogo(awayTeamId);
                  if (logo) {
                    return <img src={logo} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-[#0f9d3d]/40" />;
                  }
                  const icon = getTeamIcon(awayTeamId);
                  if (icon) {
                    return (
                      <div className="w-12 h-12 rounded-full flex items-center justify-center border border-white/10" style={{ backgroundColor: icon.bgColor }}>
                        <svg className="w-6 h-6" viewBox="0 0 24 24" style={{ color: icon.iconColor }} dangerouslySetInnerHTML={{ __html: icon.svg }} />
                      </div>
                    );
                  }
                  const name = getTeamName(awayTeamId);
                  return (
                    <div className="w-12 h-12 rounded-full bg-[#0f9d3d]/20 flex items-center justify-center border-2 border-[#0f9d3d]/40">
                      <span className="text-[#0f9d3d] font-bold text-lg">{name.charAt(0)}</span>
                    </div>
                  );
                })()}
                <span className="text-white text-sm font-semibold leading-tight">
                  {nextFixture.team_b_name || nextFixture.away_team_name || getTeamName(awayTeamId)}
                </span>
              </div>
            </div>

            {/* Match details */}
            <div className="mt-4 flex flex-col items-center gap-1.5">
              {nextFixture.scheduled_at ? (
                <span className="text-green-300 text-xs font-medium">
                  {new Date(nextFixture.scheduled_at).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                  {" "}at{" "}
                  {new Date(nextFixture.scheduled_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                </span>
              ) : nextFixture.match_date ? (
                <span className="text-green-300 text-xs font-medium">
                  {new Date(nextFixture.match_date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                </span>
              ) : null}
              {nextFixture.venue && (
                <span className="text-green-300/60 text-xs flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  </svg>
                  {nextFixture.venue}
                </span>
              )}
            </div>

            {/* Action button */}
            <div className="mt-4 flex justify-center">
              <button
                onClick={() => onStartMatch(nextFixture)}
                className="bg-[#0f9d3d]/20 hover:bg-[#0f9d3d]/30 border border-[#0f9d3d]/40 text-[#0f9d3d] px-5 py-2 rounded-lg text-sm font-semibold transition-colors"
              >
                {nextFixture.match_id ? "Open Match" : "View Fixture"}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-[#064428]/30 rounded-xl border border-[#0b5c33]/30 p-6 text-center">
          <p className="text-green-300/60 text-sm">No upcoming fixtures scheduled</p>
          <button
            onClick={() => onTabChange("fixtures")}
            className="text-[#0f9d3d] text-sm font-semibold mt-2 hover:underline"
          >
            Create Fixtures
          </button>
        </div>
      )}

      {/* Mini League Table (Top 4) */}
      {tableEntries.length > 0 && (
        <div className="bg-[#064428]/50 rounded-xl border border-[#0b5c33]/50 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-[#0b5c33]/40 flex items-center justify-between">
            <span className="text-xs font-bold text-[#0f9d3d] uppercase tracking-wider">League Table</span>
            <span className="text-[10px] text-green-300/50">Top {tableEntries.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-green-300/60 text-[11px]">
                  <th className="px-3 py-2 text-left font-semibold">#</th>
                  <th className="px-3 py-2 text-left font-semibold">Team</th>
                  <th className="px-3 py-2 text-center font-semibold">P</th>
                  <th className="px-3 py-2 text-center font-semibold">W</th>
                  <th className="px-3 py-2 text-center font-semibold">L</th>
                  <th className="px-3 py-2 text-center font-semibold">Pts</th>
                  <th className="px-3 py-2 text-center font-semibold">NRR</th>
                </tr>
              </thead>
              <tbody>
                {tableEntries.map((entry, idx) => {
                  const nrr = standings?.find(s => s.team_id === entry.team_id)?.net_run_rate;
                  return (
                    <tr key={entry.id} className="border-t border-[#0b5c33]/20">
                      <td className="px-3 py-2 text-green-400 font-bold text-xs">{idx + 1}</td>
                      <td className="px-3 py-2 text-white font-medium text-xs truncate max-w-[120px]">{entry.team_name}</td>
                      <td className="px-3 py-2 text-center text-green-300/80 text-xs">{entry.played}</td>
                      <td className="px-3 py-2 text-center text-green-400 text-xs">{entry.won}</td>
                      <td className="px-3 py-2 text-center text-red-400/80 text-xs">{entry.lost}</td>
                      <td className="px-3 py-2 text-center text-[#0f9d3d] font-bold text-xs">{entry.points}</td>
                      <td className={`px-3 py-2 text-center text-xs font-mono ${
                        nrr !== undefined && nrr !== null
                          ? nrr > 0 ? 'text-green-400' : nrr < 0 ? 'text-red-400' : 'text-green-300/60'
                          : 'text-green-300/60'
                      }`}>
                        {nrr !== undefined && nrr !== null ? (nrr > 0 ? '+' : '') + nrr.toFixed(2) : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 border-t border-[#0b5c33]/30">
            <button
              onClick={() => onTabChange("table")}
              className="text-[#0f9d3d] text-xs font-semibold hover:underline"
            >
              View Full Table
            </button>
          </div>
        </div>
      )}

      {/* Quick Access Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:col-span-2">
        <button
          onClick={() => onTabChange("fixtures")}
          className="bg-[#064428]/40 hover:bg-[#064428]/60 border border-[#0b5c33]/50 rounded-xl p-4 text-left transition-colors"
        >
          <svg className="w-6 h-6 text-[#0f9d3d] mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="text-white text-sm font-semibold block">Fixtures</span>
          <span className="text-green-300/50 text-[11px]">Upcoming matches</span>
        </button>

        <button
          onClick={() => onTabChange("results")}
          className="bg-[#064428]/40 hover:bg-[#064428]/60 border border-[#0b5c33]/50 rounded-xl p-4 text-left transition-colors"
        >
          <svg className="w-6 h-6 text-[#0f9d3d] mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138c.11.73.39 1.398.806 1.946a3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
          <span className="text-white text-sm font-semibold block">Results</span>
          <span className="text-green-300/50 text-[11px]">Completed matches</span>
        </button>

        <button
          onClick={() => onTabChange("teams")}
          className="bg-[#064428]/40 hover:bg-[#064428]/60 border border-[#0b5c33]/50 rounded-xl p-4 text-left transition-colors"
        >
          <svg className="w-6 h-6 text-[#0f9d3d] mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <span className="text-white text-sm font-semibold block">Teams</span>
          <span className="text-green-300/50 text-[11px]">{teams.length} teams</span>
        </button>

        <button
          onClick={() => onTabChange("table")}
          className="bg-[#064428]/40 hover:bg-[#064428]/60 border border-[#0b5c33]/50 rounded-xl p-4 text-left transition-colors"
        >
          <svg className="w-6 h-6 text-[#0f9d3d] mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <span className="text-white text-sm font-semibold block">Standings</span>
          <span className="text-green-300/50 text-[11px]">Full league table</span>
        </button>
      </div>
    </div>
  );
}

// League Table View Component
function LeagueTableView({ entries, standings }: { entries: LeagueTableEntry[]; standings?: LeagueStanding[] }) {
  // Build NRR lookup from standings
  const nrrMap = new Map<string, number>();
  if (standings) {
    for (const s of standings) {
      nrrMap.set(s.team_id, s.net_run_rate);
    }
  }

  // Use standings data if available (has NRR), otherwise fall back to entries
  const tableRows = standings && standings.length > 0
    ? standings
        .map(s => ({
          id: s.id,
          team_name: s.team_name || 'Unknown',
          played: s.matches_played,
          won: s.wins,
          lost: s.losses,
          tied: s.ties,
          no_result: s.no_results,
          points: s.points,
          nrr: s.net_run_rate,
        }))
        .sort((a, b) => b.points - a.points || b.nrr - a.nrr)
    : entries.map(e => ({
        id: e.id,
        team_name: e.team_name,
        played: e.played,
        won: e.won,
        lost: e.lost,
        tied: e.tied,
        no_result: e.no_result,
        points: e.points,
        nrr: nrrMap.get(e.team_id || '') ?? null,
      }));

  if (tableRows.length === 0) {
    return (
      <div className="bg-[#064428]/40 rounded-xl p-12 text-center border border-[#0b5c33]/50">
        <div className="text-5xl mb-4">📋</div>
        <h3 className="text-xl font-bold mb-2 text-white">No Table Yet</h3>
        <p className="text-green-300 text-sm">
          Add teams to your league to see the table
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-white">League Table</h3>
      </div>

      <div className="bg-[#064428]/40 rounded-xl border border-[#0b5c33]/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-white border-collapse">
            <thead className="bg-[#0b5c33]/50 text-green-200/90 uppercase tracking-wide">
              <tr>
                <th className="px-3 sm:px-4 py-3 text-center text-xs font-semibold w-12">#</th>
                <th className="px-3 py-3 text-left text-xs font-semibold">Team</th>
                <th className="px-3 py-3 text-center text-xs font-semibold">P</th>
                <th className="px-2 sm:px-3 py-3 text-center text-xs font-semibold hidden sm:table-cell">W</th>
                <th className="px-2 sm:px-3 py-3 text-center text-xs font-semibold hidden sm:table-cell">L</th>
                <th className="px-2 sm:px-3 py-3 text-center text-xs font-semibold hidden md:table-cell">T</th>
                <th className="px-2 sm:px-3 py-3 text-center text-xs font-semibold hidden md:table-cell">NR</th>
                <th className="px-3 py-3 text-center text-xs font-semibold">NRR</th>
                <th className="px-3 py-3 text-center text-xs font-semibold">Pts</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, index) => (
                <tr key={row.id} className="border-t border-[#0b5c33]/30 hover:bg-[#0b5c33]/20 transition-colors">
                  <td className="px-3 sm:px-4 py-3 text-center"><span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${index === 0 ? "bg-[#0f9d3d] text-white" : "bg-[#0b5c33]/50 text-green-300"}`}>{index + 1}</span></td>
                  <td className="px-3 sm:px-4 py-3 font-semibold whitespace-nowrap">{row.team_name}</td>
                  <td className="px-3 py-3 text-center">{row.played}</td>
                  <td className="px-2 sm:px-3 py-3 text-center tabular-nums text-green-400 hidden sm:table-cell">{row.won}</td>
                  <td className="px-2 sm:px-3 py-3 text-center tabular-nums text-red-400 hidden sm:table-cell">{row.lost}</td>
                  <td className="px-2 sm:px-3 py-3 text-center tabular-nums text-yellow-400 hidden md:table-cell">{row.tied}</td>
                  <td className="px-2 sm:px-3 py-3 text-center tabular-nums text-blue-400 hidden md:table-cell">{row.no_result}</td>
                  <td className={`px-3 py-3 text-center text-sm font-mono ${
                    row.nrr !== null && row.nrr !== undefined
                      ? row.nrr > 0 ? 'text-green-400' : row.nrr < 0 ? 'text-red-400' : 'text-slate-400'
                      : 'text-slate-500'
                  }`}>
                    {row.nrr !== null && row.nrr !== undefined
                      ? (row.nrr > 0 ? '+' : '') + row.nrr.toFixed(3)
                      : '-'}
                  </td>
                  <td className="px-3 sm:px-4 py-3 text-center font-bold text-base text-[#0f9d3d] tabular-nums">{row.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-[#0b5c33]/20 rounded-lg p-4 text-xs text-green-300">
        <div className="font-semibold mb-2">Legend:</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div><span className="font-bold">P</span> - Played</div>
          <div><span className="font-bold">W</span> - Won</div>
          <div><span className="font-bold">L</span> - Lost</div>
          <div><span className="font-bold">T</span> - Tied</div>
          <div><span className="font-bold">NR</span> - No Result</div>
          <div><span className="font-bold">NRR</span> - Net Run Rate</div>
          <div><span className="font-bold">Pts</span> - Points</div>
        </div>
      </div>
    </div>
  );
}

// Fixtures View Component
function FixturesView({
  fixtures,
  leagueId,
  onCreateFixture,
  onRefresh,
  onStartMatch,
  onGenerateFixtures,
}: {
  fixtures: LeagueFixture[];
  leagueId: string;
  onCreateFixture: () => void;
  onRefresh: () => void;
  onStartMatch: (fixture: LeagueFixture) => void;
  onGenerateFixtures: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-white">Upcoming Fixtures</h3>
        <div className="flex gap-2">
          <button
            onClick={onGenerateFixtures}
            className="bg-green-700 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors text-sm"
          >
            🎲 Generate Fixtures
          </button>
          <button
            onClick={onCreateFixture}
            className="bg-[#0f9d3d] hover:bg-green-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors text-sm"
          >
            + Create Fixture
          </button>
        </div>
      </div>

      {fixtures.length === 0 ? (
        <div className="bg-[#064428]/40 rounded-xl p-12 text-center border border-[#0b5c33]/50">
          <div className="text-5xl mb-4">📅</div>
          <h3 className="text-xl font-bold mb-2 text-white">No Fixtures Yet</h3>
          <p className="text-green-300 text-sm mb-6">
            Generate fixtures automatically or create them manually
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={onGenerateFixtures}
              className="bg-green-700 hover:bg-green-600 px-6 py-3 rounded-lg font-semibold transition-colors inline-block text-white"
            >
              🎲 Generate Fixtures
            </button>
            <button
              onClick={onCreateFixture}
              className="bg-[#0f9d3d] hover:bg-green-600 px-6 py-3 rounded-lg font-semibold transition-colors inline-block text-white"
            >
              Create Manually
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2 items-start">
          {fixtures.map((fixture) => {
            const isFinal =
              fixture.team_a_name === "Winner SF1" ||
              fixture.team_b_name === "Winner SF2" ||
              (fixture.team_a_name || "").startsWith("Winner SF") ||
              (fixture.team_b_name || "").startsWith("Winner SF");
            const isSF1 = fixture.team_a_name === "1st Place" && fixture.team_b_name === "4th Place";
            const isSF2 = fixture.team_a_name === "2nd Place" && fixture.team_b_name === "3rd Place";
            const stageLabel = isFinal ? "Final" : isSF1 ? "SF1" : isSF2 ? "SF2" : null;
            return (
            <div
              key={fixture.id}
              className={`rounded-xl p-5 border transition-colors ${stageLabel ? "lg:col-span-2 border-yellow-600/40 bg-gradient-to-br from-yellow-900/25 to-[#064428]/50" : "bg-[#064428]/40 border-[#0b5c33]/50 hover:bg-[#064428]/55"}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex-1">
                  {stageLabel ? (
                    <div className="inline-block text-xs font-bold mb-1 px-2 py-0.5 rounded bg-yellow-600/30 text-yellow-300 border border-yellow-600/40">
                      {stageLabel === "Final" ? "FINAL" : stageLabel === "SF1" ? "SEMI FINAL 1" : "SEMI FINAL 2"}
                    </div>
                  ) : fixture.round_number && (
                    <div className="text-green-500 text-xs font-semibold mb-1">
                      Round {fixture.round_number}
                    </div>
                  )}
                  {fixture.scheduled_at ? (
                    <div className="text-green-400 text-sm mb-1">
                      {new Date(fixture.scheduled_at).toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      })}{" "}
                      • {new Date(fixture.scheduled_at).toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  ) : (
                    <div className="text-yellow-500 text-sm mb-1">Date TBD</div>
                  )}
                  <div className="text-white font-semibold text-lg mb-1">
                    {fixture.team_a_name || fixture.home_team_name || "Team A"} vs{" "}
                    {fixture.team_b_name || fixture.away_team_name || "Team B"}
                  </div>
                  {fixture.venue && (
                    <div className="text-green-300 text-sm">📍 {fixture.venue}</div>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    {fixture.status === "COMPLETED" && (
                      <span className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded font-semibold">
                        ✓ COMPLETED
                      </span>
                    )}
                    {fixture.status === "IN_PROGRESS" && (
                      <span className="text-xs bg-blue-700 text-blue-300 px-2 py-1 rounded font-semibold">
                        ⚡ LIVE
                      </span>
                    )}
                    {fixture.status !== "COMPLETED" && fixture.status !== "IN_PROGRESS" && (
                      <span className="text-xs bg-green-900/40 text-green-400 px-2 py-1 rounded">
                        SCHEDULED
                      </span>
                    )}
                    {fixture.match_id && (
                      <span className="text-xs text-green-500">
                        🔗 Linked
                      </span>
                    )}
                  </div>
                </div>
                {fixture.team_a_name !== "TBD" && fixture.team_b_name !== "TBD" && (
                  <div className="flex gap-2 w-full sm:w-auto shrink-0">
                    {!fixture.match_id && fixture.status !== "COMPLETED" && (
                      <button
                        onClick={() => onStartMatch(fixture)}
                        className="bg-[#0f9d3d] hover:bg-green-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors w-full sm:w-auto"
                      >
                        Create Match
                      </button>
                    )}
                    {fixture.match_id && fixture.status === "IN_PROGRESS" && (
                      <button
                        onClick={() => onStartMatch(fixture)}
                        className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors w-full sm:w-auto"
                      >
                        Open Match
                      </button>
                    )}
                    {fixture.match_id && fixture.status === "COMPLETED" && (
                      <button
                        onClick={() => onStartMatch(fixture)}
                        className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors w-full sm:w-auto"
                      >
                        View Result
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
}

// Results View Component
function ResultsView({ fixtures, onViewResult }: { fixtures: LeagueFixture[]; onViewResult: (fixture: LeagueFixture) => void }) {
  if (fixtures.length === 0) {
    return (
      <div className="bg-[#064428]/40 rounded-xl p-12 text-center border border-[#0b5c33]/50">
        <div className="text-5xl mb-4">🏏</div>
        <h3 className="text-xl font-bold mb-2 text-white">No Results Yet</h3>
        <p className="text-green-300 text-sm">
          Completed match results will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      {fixtures.map((fixture) => (
        <div
          key={fixture.id}
          onClick={() => onViewResult(fixture)}
          className="group bg-[#064428]/40 rounded-xl p-5 border border-[#0b5c33]/50 cursor-pointer hover:bg-[#064428]/60 hover:border-[#0f9d3d]/40 transition-colors"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-green-400 text-xs font-medium">
              {fixture.match_date
                ? new Date(fixture.match_date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
                : "Date TBD"}
            </span>
            <span className="text-[10px] font-bold uppercase tracking-wide bg-gray-700/60 text-gray-200 px-2 py-0.5 rounded">Completed</span>
          </div>
          <div className="text-white font-bold text-lg leading-tight">
            {fixture.home_team_name || fixture.team_a_name || "Home Team"}{" "}
            <span className="text-green-500/70 font-normal">vs</span>{" "}
            {fixture.away_team_name || fixture.team_b_name || "Away Team"}
          </div>
          {fixture.result_text && (
            <div className="mt-2 text-sm text-[#0f9d3d] font-semibold">{fixture.result_text}</div>
          )}
          {fixture.venue && (
            <div className="text-green-300/70 text-xs mt-1">📍 {fixture.venue}</div>
          )}
          <div className="mt-3 inline-flex items-center gap-1 text-xs text-green-400 group-hover:text-[#0f9d3d] font-semibold">
            View Scorecard
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </div>
        </div>
      ))}
    </div>
  );
}

// Teams View Component
function TeamsView({
  teams,
  onAddTeam,
  onEditTeam,
  onRemoveTeam,
}: {
  teams: LeagueTeam[];
  onAddTeam: () => void;
  onEditTeam: (team: LeagueTeam) => void;
  onRemoveTeam: (teamRecordId: string) => void;
}) {
  if (teams.length === 0) {
    return (
      <div className="bg-[#064428]/40 rounded-xl p-12 text-center border border-[#0b5c33]/50">
        <div className="w-16 h-16 rounded-full bg-[#0f9d3d]/10 border border-[#0f9d3d]/30 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-[#0f9d3d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold mb-2 text-white">No Teams Yet</h3>
        <p className="text-green-300 text-sm mb-6">
          Add teams to this league to create fixtures
        </p>
        <button
          onClick={onAddTeam}
          className="bg-[#0f9d3d] hover:bg-green-600 px-6 py-3 rounded-lg font-semibold transition-colors inline-block text-white"
        >
          Add First Team
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-white">League Teams ({teams.length})</h3>
        <button
          onClick={onAddTeam}
          className="bg-[#0f9d3d] hover:bg-green-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors text-sm"
        >
          + Add Team
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {teams.map((team) => (
          <TeamCard key={team.id} team={team} onEdit={() => onEditTeam(team)} onRemove={() => onRemoveTeam(team.id)} />
        ))}
      </div>
    </div>
  );
}

function TeamCard({ team, onEdit, onRemove }: { team: LeagueTeam; onEdit: () => void; onRemove: () => void }) {
  const icon = team.team_icon_id ? getTeamIconById(team.team_icon_id) : null;

  return (
    <div
      className="bg-[#064428]/40 rounded-xl p-4 border border-[#0b5c33]/50 hover:bg-[#064428]/60 transition-colors cursor-pointer"
      onClick={onEdit}
    >
      <div className="flex items-start gap-3">
        {team.team_logo_url ? (
          <img
            src={team.team_logo_url}
            alt={team.team_name}
            className="w-12 h-12 rounded-full object-cover border-2 border-[#0f9d3d]/50 flex-shrink-0"
          />
        ) : icon ? (
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center border border-white/10 flex-shrink-0"
            style={{ backgroundColor: icon.bgColor }}
          >
            <svg
              className="w-6 h-6"
              viewBox="0 0 24 24"
              style={{ color: icon.iconColor }}
              dangerouslySetInnerHTML={{ __html: icon.svg }}
            />
          </div>
        ) : (
          <div className="w-12 h-12 rounded-full bg-[#0f9d3d]/20 flex items-center justify-center border-2 border-[#0f9d3d]/50 flex-shrink-0">
            <span className="text-[#0f9d3d] font-bold text-lg">{team.team_name.charAt(0)}</span>
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="text-white font-bold text-base truncate">{team.team_name}</div>
          {team.captain && (
            <div className="text-green-300 text-xs mt-0.5">Captain: {team.captain}</div>
          )}
          {team.vice_captain && (
            <div className="text-green-300/70 text-xs">Vice Captain: {team.vice_captain}</div>
          )}
          <div className="text-green-500/50 text-[11px] mt-1.5">
            Tap to edit
          </div>
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="text-red-400/70 hover:text-red-300 p-1.5 hover:bg-red-900/20 rounded-lg transition-colors flex-shrink-0"
          title="Remove team"
          aria-label={`Remove ${team.team_name}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// Create League Modal Component
interface CreateLeagueModalProps {
  onClose: () => void;
  onCreate: (league: Omit<League, "id" | "user_id" | "created_at">) => void | Promise<void>;
}

function CreateLeagueModal({ onClose, onCreate }: CreateLeagueModalProps) {
  const [name, setName] = useState("");
  const [format, setFormat] = useState<"INDOOR" | "T20" | "ODI">("INDOOR");
  const [overs, setOvers] = useState(18);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [location, setLocation] = useState("");

  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    if (!name.trim()) {
      alert('Please enter a league name.');
      return;
    }
    setSubmitting(true);
    try {
      await onCreate({
        name: name.trim(),
        format,
        overs,
        start_date: startDate || null,
        end_date: endDate || null,
        location: location.trim() || null,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-[60] p-4">
      <div className="bg-[#064428] rounded-xl max-w-lg w-full border border-[#0b5c33] max-h-[90vh] overflow-y-auto">
        <div className="border-b border-[#0b5c33] px-6 py-4 flex items-center justify-between sticky top-0 bg-[#064428] z-10">
          <h2 className="text-xl font-bold text-white">Create New League</h2>
          <button
            onClick={onClose}
            className="text-2xl hover:bg-[#0b5c33] rounded-lg p-2 transition-colors text-white"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-2 text-white">League Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Summer Indoor League 2025"
              className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white placeholder-green-600"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-white">Format</label>
            <select
              value={format}
              onChange={(e) => {
                const newFormat = e.target.value as "INDOOR" | "T20" | "ODI";
                setFormat(newFormat);
                if (newFormat === "INDOOR") setOvers(18);
                else if (newFormat === "T20") setOvers(20);
                else if (newFormat === "ODI") setOvers(50);
              }}
              className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white"
            >
              <option value="INDOOR">Indoor Cricket</option>
              <option value="T20">T20</option>
              <option value="ODI">ODI</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-white">Overs per Innings</label>
            <input
              type="number"
              value={overs}
              onChange={(e) => setOvers(parseInt(e.target.value) || 18)}
              min="1"
              max="50"
              className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2 text-white">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2 text-white">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-white">Location</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Central Indoor Arena"
              className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white placeholder-green-600"
            />
          </div>

          <div className="flex gap-4 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-[#012b1b] hover:bg-[#0b5c33] px-6 py-3 rounded-lg font-semibold transition-colors text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !name.trim()}
              className="flex-1 bg-[#0f9d3d] hover:bg-green-600 disabled:bg-green-900 disabled:cursor-not-allowed px-6 py-3 rounded-lg font-semibold transition-colors text-white"
            >
              {submitting ? 'Creating…' : 'Create League'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Create Fixture Modal Component
interface CreateFixtureModalProps {
  league: League;
  teams: LeagueTeam[];
  teamNames: Map<string, string>;
  onClose: () => void;
  onCreated: () => void;
}

function CreateFixtureModal({ league, teams, teamNames, onClose, onCreated }: CreateFixtureModalProps) {
  const [homeTeamId, setHomeTeamId] = useState("");
  const [awayTeamId, setAwayTeamId] = useState("");
  const [round, setRound] = useState("");
  const [matchDate, setMatchDate] = useState("");
  const [matchTime, setMatchTime] = useState("19:00");
  const [venue, setVenue] = useState("");
  const [notes, setNotes] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!homeTeamId || !awayTeamId || !matchDate || homeTeamId === awayTeamId) {
      alert("Please fill all required fields and ensure home and away teams are different");
      return;
    }

    const { createFixture } = await import("../store/leagues");
    const created = await createFixture({
      league_id: league.id,
      home_team_id: homeTeamId,
      away_team_id: awayTeamId,
      match_id: null,
      round: round || null,
      match_date: matchDate,
      match_time: matchTime,
      venue: venue || null,
      notes: notes || null,
      status: "SCHEDULED",
    });

    if (created) {
      onCreated();
      onClose();
    }
  }

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-[60] p-4">
      <div className="bg-[#064428] rounded-xl max-w-md w-full border border-[#0b5c33] max-h-[90vh] overflow-y-auto">
        <div className="border-b border-[#0b5c33] px-6 py-4 flex items-center justify-between sticky top-0 bg-[#064428]">
          <h2 className="text-xl font-bold text-white">Create Fixture</h2>
          <button
            onClick={onClose}
            className="text-2xl hover:bg-[#0b5c33] rounded-lg p-2 transition-colors text-white"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-2 text-white">Home Team *</label>
            <select
              value={homeTeamId}
              onChange={(e) => setHomeTeamId(e.target.value)}
              className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white"
              required
            >
              <option value="">Select home team</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {teamNames.get(team.id) || team.team_id}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-white">Away Team *</label>
            <select
              value={awayTeamId}
              onChange={(e) => setAwayTeamId(e.target.value)}
              className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white"
              required
            >
              <option value="">Select away team</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id} disabled={team.id === homeTeamId}>
                  {teamNames.get(team.id) || team.team_id}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-white">Round / Week</label>
            <input
              type="text"
              value={round}
              onChange={(e) => setRound(e.target.value)}
              placeholder="e.g. Round 1, Week 3"
              className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white placeholder-green-600"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2 text-white">Date *</label>
              <input
                type="date"
                value={matchDate}
                onChange={(e) => setMatchDate(e.target.value)}
                className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2 text-white">Time *</label>
              <input
                type="time"
                value={matchTime}
                onChange={(e) => setMatchTime(e.target.value)}
                className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-white">Venue</label>
            <input
              type="text"
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              placeholder="e.g. XYZ Indoor Center"
              className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white placeholder-green-600"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-white">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes..."
              rows={3}
              className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white placeholder-green-600 resize-none"
            />
          </div>

          <div className="flex gap-4 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-[#012b1b] hover:bg-[#0b5c33] px-6 py-3 rounded-lg font-semibold transition-colors text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-[#0f9d3d] hover:bg-green-600 px-6 py-3 rounded-lg font-semibold transition-colors text-white"
            >
              Create Fixture
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Add Team Modal Component
interface AddTeamModalProps {
  leagueId: string;
  onClose: () => void;
  onAdded: () => void;
  editTeam?: LeagueTeam | null;
}

function AddTeamModal({ leagueId, onClose, onAdded, editTeam }: AddTeamModalProps) {
  const isEdit = !!editTeam;
  const [teamName, setTeamName] = useState(editTeam?.team_name || "");
  const [captain, setCaptain] = useState(editTeam?.captain || "");
  const [viceCaptain, setViceCaptain] = useState(editTeam?.vice_captain || "");
  const [notes, setNotes] = useState(editTeam?.notes || "");
  const [logoMode, setLogoMode] = useState<"icon" | "upload">(editTeam?.team_logo_url ? "upload" : "icon");
  const [selectedIcon, setSelectedIcon] = useState<TeamIcon | null>(() =>
    editTeam?.team_icon_id ? (getTeamIconById(editTeam.team_icon_id) || getRandomIcon()) : getRandomIcon()
  );
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState<string | null>(editTeam?.team_logo_url || null);
  const [currentLeagueTeams, setCurrentLeagueTeams] = useState<string[]>([]);
  const [error, setError] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [successState, setSuccessState] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadCurrentLeagueTeams();
  }, []);

  async function loadCurrentLeagueTeams() {
    const teams = await getLeagueTeams(leagueId);
    const teamNames = teams
      .filter(t => !editTeam || t.id !== editTeam.id)
      .map(t => t.team_name.toLowerCase().trim());
    setCurrentLeagueTeams(teamNames);
  }

  function handleShuffle() {
    setSelectedIcon(getRandomIcon());
  }

  async function handleFileUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Image must be less than 2MB");
      return;
    }

    try {
      setUploading(true);
      setError("");

      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      await new Promise((resolve, reject) => {
        img.onload = () => { URL.revokeObjectURL(objectUrl); resolve(true); };
        img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error("Failed to load")); };
        img.src = objectUrl;
      });

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d")!;
      const size = Math.min(img.width, img.height);
      canvas.width = 256;
      canvas.height = 256;
      ctx.drawImage(img, (img.width - size) / 2, (img.height - size) / 2, size, size, 0, 0, 256, 256);

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => b ? resolve(b) : reject(new Error("Blob failed")), "image/webp", 0.85);
      });

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const fileName = `${user.id}/league_team_${Date.now()}.webp`;
      const { error: uploadError } = await supabase.storage.from("team-logos").upload(fileName, blob, { contentType: "image/webp", upsert: true });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("team-logos").getPublicUrl(fileName);
      setUploadedLogoUrl(urlData.publicUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    const trimmedName = teamName.trim();
    if (!trimmedName) {
      setError("Team name is required");
      return;
    }

    if (currentLeagueTeams.includes(trimmedName.toLowerCase())) {
      setError("This team is already in the league");
      return;
    }

    setSubmitting(true);

    const logoUrl = logoMode === "upload" ? uploadedLogoUrl : null;
    const iconId = logoMode === "icon" && selectedIcon ? selectedIcon.id : null;

    let success: boolean;

    if (isEdit && editTeam) {
      success = await updateLeagueTeam(editTeam.id, {
        team_name: trimmedName,
        team_logo_url: logoUrl,
        team_icon_id: iconId,
        captain: captain.trim() || null,
        vice_captain: viceCaptain.trim() || null,
        notes: notes.trim() || null,
      });
    } else {
      success = await addTeamToLeague(leagueId, trimmedName, {
        teamLogoUrl: logoUrl,
        teamIconId: iconId,
        captain: captain.trim() || null,
        viceCaptain: viceCaptain.trim() || null,
        notes: notes.trim() || null,
      });
    }

    if (success) {
      onAdded();
      if (isEdit) {
        onClose();
      } else {
        setSuccessState(trimmedName);
      }
    } else {
      setError(isEdit ? "Failed to update team. Please try again." : "Failed to add team. Please try again.");
    }
    setSubmitting(false);
  }

  function handleAddAnother() {
    setTeamName("");
    setCaptain("");
    setViceCaptain("");
    setNotes("");
    setSelectedIcon(getRandomIcon());
    setUploadedLogoUrl(null);
    setLogoMode("icon");
    setError("");
    setSuccessState(null);
    loadCurrentLeagueTeams();
  }

  if (successState) {
    return (
      <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-[60] p-4">
        <div className="bg-[#064428] rounded-xl max-w-md w-full border border-[#0b5c33] p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-[#0f9d3d]/20 border-2 border-[#0f9d3d] flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-[#0f9d3d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-1">Team Created</h2>
          <p className="text-green-300 text-sm mb-6">"{successState}" has been added and is ready for fixtures.</p>
          <div className="space-y-2">
            <button
              onClick={handleAddAnother}
              className="w-full bg-[#0f9d3d] hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Add Another Team
            </button>
            <button
              onClick={onClose}
              className="w-full bg-[#012b1b] hover:bg-[#0b5c33] text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Back to League
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/75 flex items-end sm:items-center justify-center z-[60] sm:p-4">
      <div className="bg-[#064428] rounded-t-2xl sm:rounded-xl max-w-md w-full border border-[#0b5c33] flex flex-col" style={{ maxHeight: "calc(100vh - env(safe-area-inset-top, 0px))" }}>
        {/* Sticky header */}
        <div className="border-b border-[#0b5c33] px-5 py-3.5 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-bold text-white">{isEdit ? "Edit Team" : "Add Team"}</h2>
          <button
            onClick={onClose}
            className="hover:bg-[#0b5c33] rounded-lg p-2 transition-colors text-white"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable form content */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-5 space-y-4" style={{ paddingBottom: "8px" }}>
            {error && (
              <div className="bg-red-900/20 border border-red-700 text-red-400 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Preview */}
            <div className="flex items-center gap-4 bg-[#012b1b] rounded-xl p-4 border border-[#0b5c33]/50">
              {logoMode === "upload" && uploadedLogoUrl ? (
                <img src={uploadedLogoUrl} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-[#0f9d3d]" />
              ) : logoMode === "icon" && selectedIcon ? (
                <TeamIconPreview icon={selectedIcon} size="lg" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-[#0f9d3d]/10 border-2 border-dashed border-[#0f9d3d]/30 flex items-center justify-center">
                  <span className="text-[#0f9d3d]/50 text-lg font-bold">?</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold text-lg truncate">
                  {teamName.trim() || "Team Name"}
                </div>
                {captain.trim() && (
                  <div className="text-green-300 text-xs">Captain: {captain.trim()}</div>
                )}
                {viceCaptain.trim() && (
                  <div className="text-green-300/70 text-xs">VC: {viceCaptain.trim()}</div>
                )}
              </div>
            </div>

            {/* Team Name */}
            <div>
              <label className="block text-sm font-semibold mb-1.5 text-white">Team Name *</label>
              <input
                type="text"
                value={teamName}
                onChange={(e) => { setTeamName(e.target.value); setError(""); }}
                placeholder="Enter team name"
                className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white placeholder-green-700"
                autoFocus
              />
            </div>

            {/* Logo */}
            <div>
              <label className="block text-sm font-semibold mb-1.5 text-white">
                Team Logo <span className="text-green-500/50 font-normal">(optional)</span>
              </label>
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setLogoMode("icon")}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                    logoMode === "icon"
                      ? "bg-[#0f9d3d] text-white"
                      : "bg-[#012b1b] text-green-300 border border-[#0b5c33] hover:bg-[#0b5c33]/50"
                  }`}
                >
                  Built-in Icons
                </button>
                <button
                  type="button"
                  onClick={() => setLogoMode("upload")}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                    logoMode === "upload"
                      ? "bg-[#0f9d3d] text-white"
                      : "bg-[#012b1b] text-green-300 border border-[#0b5c33] hover:bg-[#0b5c33]/50"
                  }`}
                >
                  Upload Logo
                </button>
              </div>

              {logoMode === "icon" && (
                <TeamIconPicker
                  selectedIconId={selectedIcon?.id || null}
                  onSelect={(icon: TeamIcon) => setSelectedIcon(icon)}
                  onShuffle={handleShuffle}
                />
              )}

              {logoMode === "upload" && (
                <div className="space-y-3">
                  {uploadedLogoUrl ? (
                    <div className="flex items-center gap-3 bg-[#012b1b] p-3 rounded-lg border border-[#0b5c33]">
                      <img src={uploadedLogoUrl} alt="" className="w-10 h-10 rounded-full object-cover" />
                      <span className="text-green-300 text-sm flex-1">Logo uploaded</span>
                      <button
                        type="button"
                        onClick={() => setUploadedLogoUrl(null)}
                        className="text-red-400 text-xs hover:text-red-300"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="w-full bg-[#012b1b] border-2 border-dashed border-[#0b5c33] rounded-lg px-4 py-5 text-center hover:border-[#0f9d3d]/50 transition-colors disabled:opacity-50"
                    >
                      {uploading ? (
                        <span className="text-green-300 text-sm">Uploading...</span>
                      ) : (
                        <>
                          <svg className="w-7 h-7 mx-auto text-green-500/50 mb-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="text-green-300 text-sm">Tap to upload team logo</span>
                          <span className="text-green-500/50 text-xs block mt-0.5">PNG, JPEG, WebP - Max 2MB</span>
                        </>
                      )}
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>
              )}
            </div>

            {/* Captain */}
            <div>
              <label className="block text-sm font-semibold mb-1.5 text-white">
                Captain <span className="text-green-500/50 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={captain}
                onChange={(e) => setCaptain(e.target.value)}
                placeholder="Captain name"
                className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white placeholder-green-700"
              />
            </div>

            {/* Vice Captain */}
            <div>
              <label className="block text-sm font-semibold mb-1.5 text-white">
                Vice Captain <span className="text-green-500/50 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={viceCaptain}
                onChange={(e) => setViceCaptain(e.target.value)}
                placeholder="Vice captain name"
                className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white placeholder-green-700"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-semibold mb-1.5 text-white">
                Notes <span className="text-green-500/50 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Short team note"
                className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white placeholder-green-700"
              />
            </div>
          </div>

          {/* Sticky save bar */}
          <div className="flex-shrink-0 border-t border-[#0b5c33] px-5 py-3 bg-[#064428]" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-[#012b1b] hover:bg-[#0b5c33] px-4 py-3 rounded-lg font-semibold transition-colors text-white text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !teamName.trim()}
                className="flex-1 bg-[#0f9d3d] hover:bg-green-600 disabled:bg-green-900 disabled:cursor-not-allowed px-4 py-3 rounded-lg font-semibold transition-colors text-white text-sm"
              >
                {submitting ? "Saving..." : isEdit ? "Save Changes" : "Save Team"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

interface GenerateFixturesModalProps {
  league: League;
  teams: LeagueTeam[];
  existingFixtures: LeagueFixture[];
  onClose: () => void;
  onGenerated: () => void;
}

function GenerateFixturesModal({ league, teams, existingFixtures, onClose, onGenerated }: GenerateFixturesModalProps) {
  const [competitionType, setCompetitionType] = useState<CompetitionType>(
    (league.competition_type as CompetitionType) || "round_robin"
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  async function handleGenerate() {
    if (teams.length < 2) {
      setError("You need at least 2 teams to generate fixtures");
      return;
    }

    if (existingFixtures.length > 0) {
      const confirmed = confirm(
        `This league already has ${existingFixtures.length} fixture(s). Do you want to delete them and generate new fixtures?`
      );
      if (!confirmed) return;
    }

    setLoading(true);
    setError("");

    try {
      if (existingFixtures.length > 0) {
        await deleteAllLeagueFixtures(league.id);
      }

      const generatedFixtures = generateFixturesByType(competitionType, teams);

      if (generatedFixtures.length === 0) {
        setError("No fixtures were generated. Please check your selection.");
        setLoading(false);
        return;
      }

      const success = await generateLeagueFixtures(league.id, generatedFixtures);

      if (success) {
        onGenerated();
        onClose();
      } else {
        setError("Failed to generate fixtures. Please try again.");
      }
    } catch (err) {
      console.error("Error generating fixtures:", err);
      setError("An error occurred while generating fixtures.");
    }

    setLoading(false);
  }

  const fixtureCount = teams.length < 2 ? 0 :
    competitionType === "round_robin" ? (teams.length * (teams.length - 1)) / 2 :
    competitionType === "double_round_robin" ? teams.length * (teams.length - 1) :
    competitionType === "league_final" ? (teams.length * (teams.length - 1)) / 2 + 1 :
    competitionType === "ROUND_ROBIN_PLAYOFFS" ? (teams.length * (teams.length - 1)) / 2 + 3 :
    0;

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-[60] p-4">
      <div className="bg-[#064428] rounded-xl max-w-md w-full border border-[#0b5c33]">
        <div className="border-b border-[#0b5c33] px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Generate Fixtures</h2>
          <button
            onClick={onClose}
            className="text-2xl hover:bg-[#0b5c33] rounded-lg p-2 transition-colors text-white"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-900/20 border border-red-700 text-red-400 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="bg-[#0b5c33]/30 rounded-lg p-4 text-sm text-green-300">
            <div className="flex justify-between mb-2">
              <span>League:</span>
              <span className="font-semibold text-white">{league.name}</span>
            </div>
            <div className="flex justify-between">
              <span>Teams:</span>
              <span className="font-semibold text-white">{teams.length}</span>
            </div>
            {existingFixtures.length > 0 && (
              <div className="flex justify-between mt-2 text-yellow-400">
                <span>Existing Fixtures:</span>
                <span className="font-semibold">{existingFixtures.length}</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-white">
              Competition Type
            </label>
            <select
              value={competitionType}
              onChange={(e) => setCompetitionType(e.target.value as CompetitionType)}
              className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] text-white"
              disabled={loading}
            >
              <option value="round_robin">Round Robin (everyone plays once)</option>
              <option value="double_round_robin">Double Round Robin (everyone plays twice)</option>
              <option value="league_final">League + Final (round robin + final match)</option>
              <option value="ROUND_ROBIN_PLAYOFFS">League + Playoffs (Top 4 → Semi Finals → Final)</option>
              <option value="custom" disabled>Custom Fixtures (coming soon)</option>
            </select>
          </div>

          {teams.length >= 2 && (
            <div className="bg-green-900/20 border border-green-700 text-green-300 px-4 py-3 rounded-lg text-sm">
              <div className="font-semibold mb-1">Preview:</div>
              <div>
                {competitionType === "round_robin" && (
                  <span>Will generate {fixtureCount} fixtures (single round robin)</span>
                )}
                {competitionType === "double_round_robin" && (
                  <span>Will generate {fixtureCount} fixtures across 2 rounds</span>
                )}
                {competitionType === "league_final" && (
                  <span>Will generate {fixtureCount} fixtures (league + final TBD)</span>
                )}
                {competitionType === "ROUND_ROBIN_PLAYOFFS" && (
                  <span>Round robin stage, then top 4 teams advance to semi-finals and final ({fixtureCount} fixtures)</span>
                )}
                {competitionType === "custom" && (
                  <span>Custom fixture builder coming soon</span>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-4 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 bg-[#012b1b] hover:bg-[#0b5c33] px-6 py-3 rounded-lg font-semibold transition-colors text-white disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={loading || teams.length < 2}
              className="flex-1 bg-[#0f9d3d] hover:bg-green-600 px-6 py-3 rounded-lg font-semibold transition-colors text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Generating..." : "Generate"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
